import { useRef, useCallback, useState, useEffect } from "react";

interface UseAudioRecorderOptions {
  onAudioChunk: (blob: Blob) => void;
  chunkDuration: number; // milliseconds
}

interface UseAudioRecorderReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<void>;
  isSupported: boolean;
  isRecording: boolean;
}

export const useAudioRecorder = ({
  onAudioChunk,
  chunkDuration,
}: UseAudioRecorderOptions): UseAudioRecorderReturn => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isSupported] = useState(() => {
    return !!(navigator.mediaDevices && "MediaRecorder" in window);
  });

  const startRecording = useCallback(async () => {
    if (!isSupported) {
      throw new Error("Audio recording not supported");
    }

    if (isRecording) {
      return; // Already recording
    }

    try {
      // Get user media
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;

      // Create MediaRecorder with timeslice for regular chunks
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });

      mediaRecorderRef.current = mediaRecorder;

      // Handle data available - this fires automatically every chunkDuration when using start(timeslice)
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          onAudioChunk(event.data);
        }
      };

      // Handle errors
      mediaRecorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setIsRecording(false);
      };

      // Start recording with automatic chunking - timeslice parameter makes it emit data events every chunkDuration ms
      mediaRecorder.start(chunkDuration);
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting audio recording:", error);
      setIsRecording(false);
      throw error;
    }
  }, [isSupported, isRecording, onAudioChunk, chunkDuration]);

  const stopRecording = useCallback(async () => {
    setIsRecording(false);

    // Stop media recorder - this will trigger final ondataavailable
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }

    // Stop stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    mediaRecorderRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === "recording"
      ) {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    startRecording,
    stopRecording,
    isSupported,
    isRecording,
  };
};
